import axios from 'axios';
import { useCallback, useMemo, useState } from 'react';
import Form, { Validations } from '../../../../../components/Form';
import { ControlItem } from '../../../types';
import { formatParam } from '../../helpers';

import ServicePaths from '../../../../../bridge/utils/ServicePaths';

const AddBalanceForm = () => {
  const [userId, setUserId] = useState<string | undefined>();
  const [botId, setBotId] = useState<string | undefined>();
  const [balance, setBalance] = useState<string | undefined>();
  const [balanceType, setBalanceType] = useState<string | undefined>();

  const [errors, setErrors] = useState<Record<string, Validations>>({});
  const [formMessage, setFormMessage] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const checkBalanceValidation = useCallback(() => {
    /** userId can not be empty */
    let didError;

    setErrors({});

    if (typeof userId === 'undefined' || userId?.length === 0) {
      setErrors((prev) => ({
        ...prev,
        userId: Validations.Required,
      }));

      didError = true;
    }

    if (typeof botId === 'undefined' || botId?.length === 0) {
      setErrors((prev) => ({
        ...prev,
        botId: Validations.Required,
      }));

      didError = true;
    }

    if (typeof balance === 'undefined' || balance?.length === 0) {
      setErrors((prev) => ({
        ...prev,
        balance: Validations.Required,
      }));

      didError = true;
    } else if (typeof balance !== 'undefined') {
      const balanceValue = parseFloat(balance);

      if (isNaN(balanceValue)) {
        setErrors((prev) => ({
          ...prev,
          balance: Validations.NumberTyping,
        }));

        didError = true;
      }
    }

    if (typeof balanceType === 'undefined' || balanceType?.length === 0) {
      setErrors((prev) => ({
        ...prev,
        balanceType: Validations.Required,
      }));

      didError = true;
    } else if (balanceType !== 'asset' && balanceType !== 'stable') {
      setErrors((prev) => ({
        ...prev,
        balanceType: Validations.BalanceTypeTyping,
      }));

      didError = true;
    }

    return !didError;
  }, [balance, balanceType, botId, userId]);

  const handleAddBalanceSubmit = useCallback(async () => {
    setIsLoading(true);
    const canContinue = checkBalanceValidation();

    if (!canContinue) {
      setIsLoading(false);
      return;
    }

    const { data } = await axios({
      method: 'POST',
      url:
        balanceType === 'stable'
          ? ServicePaths.CONTROL.AddStableBalance
          : ServicePaths.CONTROL.AddAssetBalance,
      params: {
        userId: formatParam(userId),
        botId: formatParam(botId),
        balance: formatParam(balance),
      },
    });

    setIsLoading(false);
    setFormMessage(data);
  }, [balance, balanceType, botId, checkBalanceValidation, userId]);

  const addBalanceConfig = useMemo(() => {
    return {
      title: ControlItem.AddBalance,
      formMessage: formMessage,
      fields: [
        {
          label: 'User Id',
          value: userId,
          handleValueChange: setUserId,
          error: errors.userId,
        },
        {
          label: 'Bot Id',
          value: botId,
          handleValueChange: setBotId,
          error: errors.botId,
        },
        {
          label: 'Balance',
          value: balance,
          handleValueChange: setBalance,
          error: errors.balance,
        },
        {
          label: 'Balance Type',
          value: balanceType,
          handleValueChange: setBalanceType,
          error: errors.balanceType,
        },
      ],
      handleSubmit: handleAddBalanceSubmit,
      isLoading,
    };
  }, [
    balance,
    balanceType,
    botId,
    errors.balance,
    errors.balanceType,
    errors.botId,
    errors.userId,
    formMessage,
    handleAddBalanceSubmit,
    isLoading,
    userId,
  ]);

  return <Form config={addBalanceConfig} />;
};

export default AddBalanceForm;
